import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { Bot, BookOpen, Check, ChevronDown, Columns3, Eye, FolderOpen, GitBranch, Globe, Key, Terminal as TerminalIcon } from 'lucide-react'
import type { PanelType } from './PanelToggle'
import { cn } from '../../lib/utils'

/** Sections the mobile workspace can show. 'main' is the chat/terminal column. */
export type MobileSection = 'main' | PanelType

const SECTION_META: Record<Exclude<MobileSection, 'main'>, { label: string; icon: typeof Eye }> = {
  preview: { label: 'Preview', icon: Eye },
  browser: { label: 'Server Browser', icon: Globe },
  files: { label: 'Files', icon: FolderOpen },
  docs: { label: 'Docs', icon: BookOpen },
  git: { label: 'Source & Release', icon: GitBranch },
  secrets: { label: 'App secrets', icon: Key },
}

/**
 * Chat/Preview toggle, sitting beside the work-area dropdown.
 *
 * Replaces the two tabs that dominated a full-width tab row costing ~43px of
 * an 844px screen — on the surface where vertical space is scarcest. These are
 * the two sections swapped between all day; everything else lives in
 * `SectionMenu` on the other side of the toolbar.
 */
export function MainSectionToggle({
  section,
  onSelect,
  availablePanels,
  mainIsChat,
}: {
  section: MobileSection
  onSelect: (section: MobileSection) => void
  availablePanels: PanelType[]
  /**
   * Is the main column currently showing the chat (vs the terminal)?
   *
   * This is the RESOLVED view — `useMainView`'s per-work-area override, not
   * the `claude_panel_enabled` setting it falls back to. Wiring the label to
   * the setting made the toggle say "Terminal" while the chat was on screen,
   * for anyone whose view came from an override rather than the default.
   */
  mainIsChat: boolean
}) {
  const MainIcon = mainIsChat ? Bot : TerminalIcon
  const hasPreview = availablePanels.includes('preview')

  const segment = (active: boolean) =>
    cn(
      'flex items-center gap-1 rounded px-2 py-1 text-xs font-medium transition-colors',
      active ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground',
    )

  return (
    <div className="flex items-center gap-0.5 rounded-md bg-secondary/60 p-0.5">
      <button
        type="button"
        onClick={() => onSelect('main')}
        aria-pressed={section === 'main'}
        title={mainIsChat ? 'Show the chat' : 'Show the terminal'}
        className={segment(section === 'main')}
      >
        <MainIcon className="h-3.5 w-3.5" />
        <span>{mainIsChat ? 'Chat' : 'Terminal'}</span>
      </button>
      {hasPreview && (
        <button
          type="button"
          onClick={() => onSelect('preview')}
          aria-pressed={section === 'preview'}
          title="Show the app preview"
          className={segment(section === 'preview')}
        >
          <Eye className="h-3.5 w-3.5" />
          <span>Preview</span>
        </button>
      )}
    </div>
  )
}

/**
 * The remaining sections, in the right-hand toolbar group beside the actions
 * menu — the slot (and the panels icon) the old Panels menu occupied, so the
 * control stayed where the muscle memory already was.
 *
 * It SWITCHES to a section rather than toggling whether it appears in a tab
 * row: picking "Docs" should show docs, which is what picking Docs means.
 * Activating the panel is a side effect handled by the caller, so this stays a
 * navigation control rather than a visibility one.
 */
export function SectionMenu({
  section,
  onSelect,
  availablePanels,
}: {
  section: MobileSection
  onSelect: (section: MobileSection) => void
  availablePanels: PanelType[]
}) {
  // Preview already has a segment in the toggle; listing it here too would
  // give one destination two controls.
  const overflow = availablePanels.filter((p) => p !== 'preview')
  if (overflow.length === 0) return null
  const current = section !== 'main' && section !== 'preview' ? SECTION_META[section] : null

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          title="Go to another section"
          aria-label="Sections"
          className="flex items-center gap-1.5 rounded px-1.5 py-1 text-sm text-foreground/70 transition-colors hover:bg-secondary hover:text-foreground"
        >
          <Columns3 className="h-3.5 w-3.5" />
          {/* Standing on one of these sections: name it, since the tab row
              that used to say where you are is gone. */}
          {current && <span className="text-xs font-medium text-foreground">{current.label}</span>}
          <ChevronDown className="h-3 w-3" />
        </button>
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          className="z-50 w-48 rounded-lg border border-border bg-card py-1 shadow-lg"
          align="end"
          sideOffset={4}
        >
          {overflow.map((panel) => {
            const meta = SECTION_META[panel]
            return (
              <DropdownMenu.Item
                key={panel}
                className="flex w-full cursor-pointer items-center gap-2 px-3 py-2 text-left text-sm outline-none hover:bg-secondary"
                onSelect={() => onSelect(panel)}
              >
                <meta.icon className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="flex-1">{meta.label}</span>
                {section === panel && <Check className="h-3.5 w-3.5 text-primary" />}
              </DropdownMenu.Item>
            )
          })}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  )
}
